import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import Tooltip from '../Shared/CopyableTooltip';
import EditOutlined from "@mui/icons-material/EditOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import CheckOutlined from "@mui/icons-material/CheckOutlined";
import adminTicketService from "../../services/adminTicketService";
import { TICKET_STATE } from "../../services/adminTicketUtils";
import { useUI } from "../Context/UIContext";
import { usePermissions } from "../Context/permissionsProvider";
import { roundActionIconSx } from "./ticketChatConstants";

const SELF_OPTION_VALUE = "self";

const assigneeLabelSx = {
  fontFamily: "Rubik",
  fontSize: 14,
  color: "#808080",
  backgroundColor: "#f7f9fc",
  borderRadius: 2,
  padding: 1,
};

const TicketAssign = ({ ticket, loginId, onAssigned }) => {
  const { showError, showSuccess } = useUI();
  const { ticketPermissions } = usePermissions();
  const { canSelfAssign, canAssignToOthers } = ticketPermissions;
  const [assignees, setAssignees] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [changeMode, setChangeMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!canAssignToOthers) return;
    adminTicketService
      .listAssignees()
      .then((res) => setAssignees(res.items ?? []))
      .catch((e) => showError(e?.message ?? "Errore caricamento admin assegnabili"));
  }, [canAssignToOthers, showError]);

  const assignOptions = useMemo(() => {
    const opts = [];
    if (canSelfAssign) {
      opts.push({ value: SELF_OPTION_VALUE, label: "Te stesso" });
    }
    if (canAssignToOthers) {
      for (const admin of assignees) {
        if (Number(admin.loginId) === Number(loginId)) {
          continue;
        }
        opts.push({ value: String(admin.loginId), label: admin.userName });
      }
    }
    return opts;
  }, [assignees, canAssignToOthers, canSelfAssign, loginId]);

  if (!canSelfAssign && !canAssignToOthers) return null;

  const isOpen = ticket.state === TICKET_STATE.APERTO;
  const isInProgress = ticket.state === TICKET_STATE.IN_LAVORAZIONE;
  const isClosed = ticket.state === TICKET_STATE.CHIUSO;

  const assigneeLabel = Number(ticket.operatorId) === Number(loginId)
    ? "Te stesso"
    : (ticket.assigneeUserName || "Assegnatario");

  const handleAssign = async () => {
    if (!selectedId) return;
    const operatorId = selectedId === SELF_OPTION_VALUE ? loginId : Number(selectedId);
    setSubmitting(true);
    try {
      await adminTicketService.assignTicket({ ticketId: ticket.id, operatorId });
      showSuccess("Ticket assegnato correttamente");
      setChangeMode(false);
      setSelectedId("");
      onAssigned?.();
    } catch (e) {
      showError(e?.response?.data?.message ?? e?.message ?? "Errore assegnazione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelChange = () => {
    setChangeMode(false);
    setSelectedId("");
  };

  const assigneeReadOnlyBox = (
    <Box
      mt={2}
      py={0}
      px={0}
      width={{ sm: "100%", md: "auto" }}
      maxWidth={{ sm: "100%", md: "66%" }}
      sx={{ ml: { md: "auto" }, mr: 0 }}
    >
      <Box display="flex" alignItems="center" justifyContent="right">
        <Typography sx={assigneeLabelSx}>
          Assegnato a{" "}
          <Box component="span" sx={{ fontSize: 18, fontWeight: 700, color: "#0b3d87" }}>
            {assigneeLabel}
          </Box>
        </Typography>
      </Box>
    </Box>
  );

  if (isClosed) {
    return ticket.operatorId == null ? null : assigneeReadOnlyBox;
  }

  if (isInProgress && !changeMode) {
    return (
      <Box
        mt={2}
        py={0}
        px={0}
        width={{ sm: "100%", md: "auto" }}
        maxWidth={{ sm: "100%", md: "66%" }}
        sx={{ ml: { md: "auto" }, mr: 0 }}
      >
        <Box display="flex" alignItems="center" justifyContent="right" gap={1}>
          <Typography sx={assigneeLabelSx}>
            Assegnato a{" "}
            <Box component="span" sx={{ fontSize: 18, fontWeight: 700, color: "#0b3d87" }}>
              {assigneeLabel}
            </Box>
          </Typography>
          {canAssignToOthers ? (
            <Tooltip title="Cambia assegnazione">
              <IconButton onClick={() => setChangeMode(true)} sx={roundActionIconSx} size="small">
                <EditOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>
    );
  }

  if (!isOpen && !(isInProgress && changeMode)) return null;

  return (
    <Box
      mt={2}
      py={0}
      px={0}
      width={{ sm: "100%", md: "auto" }}
      maxWidth={{ sm: "100%", md: "33%" }}
      sx={{ ml: { md: "auto" }, mr: 0 }}
    >
      <Box display="flex" gap={1} alignItems="center" justifyContent="flex-end">
        <Typography sx={assigneeLabelSx}>
          Assegna a
        </Typography>
        <TextField
          select
          size="small"
          value={selectedId}
          onChange={(e) => setSelectedId(e.target.value)}
          placeholder="Assegna"
          sx={{ fontFamily: "Rubik", backgroundColor: "#fff", borderRadius: 1, minWidth: 150 }}
        >
          {assignOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title="Assegna ticket e passa in lavorazione">
          <span>
            <IconButton
              disabled={!selectedId || submitting}
              onClick={handleAssign}
              sx={roundActionIconSx}
            >
              <CheckOutlined />
            </IconButton>
          </span>
        </Tooltip>
        {isInProgress && changeMode ? (
          <Tooltip title="Annulla modifica">
            <IconButton onClick={handleCancelChange} sx={roundActionIconSx}>
              <CloseOutlined />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

TicketAssign.propTypes = {
  ticket: PropTypes.object.isRequired,
  loginId: PropTypes.number,
  onAssigned: PropTypes.func,
};

TicketAssign.defaultProps = {
  loginId: null,
  onAssigned: null,
};

export default TicketAssign;
